import { useState, useEffect, useRef, useCallback } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, Send, Mic, Sparkles, Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/useToast';
import { apiFetch } from '../lib/api';
import { useTenantFormat } from '../hooks/useTenantFormat';

/* ────────────────────────────────────────────────────────────
   Phase 13 · "Add via AI" — multi-turn chat drawer for
   creating sales / stock / purchase records via natural language.
   ──────────────────────────────────────────────────────────── */

const INTRO_TEXT =
  'Tell me what to add. Examples: "Sold 2kg Cardamom to Kiran for ₹1400", ' +
  '"Add 50 units of Turmeric from Ram Traders at ₹80", ' +
  '"Received 30 candles into stock".';

/* ── Speech recognition helper ── */
const SpeechRec = typeof window !== 'undefined'
  ? window.SpeechRecognition || window.webkitSpeechRecognition
  : null;

export default function AddViaAIDialog({ open, onClose }) {
  const { token } = useAuth();
  const toast = useToast();

  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const [bloomVisible, setBloomVisible] = useState(false);
  const [listening, setListening] = useState(false);

  const chatRef = useRef(null);
  const inputRef = useRef(null);
  const recRef = useRef(null);

  /* Reset on open */
  useEffect(() => {
    if (open) {
      setMessages([{ role: 'assistant', kind: 'info', text: INTRO_TEXT }]);
      setSessionId(null);
      setInput('');
      setIsTyping(false);
      setIsExecuting(false);
      setBloomVisible(false);
      setTimeout(() => inputRef.current?.focus(), 200);
    } else {
      // Cleanup speech rec on close
      if (recRef.current) { try { recRef.current.abort(); } catch { /* ok */ } }
      setListening(false);
    }
  }, [open]);

  /* Auto-scroll on new messages */
  useEffect(() => {
    if (chatRef.current) {
      requestAnimationFrame(() => {
        chatRef.current.scrollTop = chatRef.current.scrollHeight;
      });
    }
  }, [messages, isTyping]);

  /* ── Submit handler ── */
  const submit = useCallback(async (text) => {
    if (!token || !text.trim() || isTyping || isExecuting) return;
    const trimmed = text.trim();
    setInput('');

    const userMsg = { role: 'user', text: trimmed };
    setMessages(prev => [...prev, userMsg]);
    setIsTyping(true);

    try {
      const body = {};
      if (sessionId) {
        body.session_id = sessionId;
        body.clarification_answer = trimmed;
      } else {
        body.text = trimmed;
      }

      const r = await apiFetch('/ai/command', {
        method: 'POST', body, token, timeoutMs: 60000,
      });

      if (r.session_id) setSessionId(r.session_id);

      if (r.status === 'clarification_needed') {
        setMessages(prev => [...prev, {
          role: 'assistant', kind: 'clarify',
          text: r.question || 'Could you share more detail?',
        }]);
      } else if (r.status === 'ready') {
        setMessages(prev => [...prev, {
          role: 'assistant', kind: 'ready',
          text: r.human_summary,
          preview: r.preview,
          intent: r.intent,
        }]);
      } else if (r.status === 'error') {
        setMessages(prev => [...prev, {
          role: 'assistant', kind: 'error',
          text: r.message || "Sorry — I couldn't understand that.",
        }]);
      }
    } catch (e) {
      setMessages(prev => [...prev, {
        role: 'assistant', kind: 'error',
        text: e?.message || 'Something went wrong.',
      }]);
      toast.show(e?.message || 'Request failed', 'error');
    } finally {
      setIsTyping(false);
    }
  }, [token, sessionId, isTyping, isExecuting, toast]);

  /* ── Approve handler ── */
  const approve = useCallback(async () => {
    if (!token || !sessionId || isExecuting) return;
    setIsExecuting(true);

    try {
      const r = await apiFetch('/ai/command/execute', {
        method: 'POST', body: { session_id: sessionId }, token, timeoutMs: 60000,
      });

      if (r.status === 'executed') {
        setBloomVisible(true);
        toast.show(r.human_summary || `${r.record_type} created`, 'success');
        setTimeout(() => {
          onClose();
        }, 1200);
      } else {
        toast.show(r.message || 'Approve failed', 'error');
      }
    } catch (e) {
      toast.show(e?.message || 'Approve failed', 'error');
    } finally {
      setIsExecuting(false);
    }
  }, [token, sessionId, isExecuting, toast, onClose]);

  /* ── Voice input ── */
  const startVoice = useCallback(() => {
    if (!SpeechRec || listening) return;
    const rec = new SpeechRec();
    rec.lang = 'en-IN';
    rec.interimResults = false;
    rec.maxAlternatives = 1;
    rec.onresult = (e) => {
      const transcript = e.results?.[0]?.[0]?.transcript;
      if (transcript) setInput(transcript);
      setListening(false);
    };
    rec.onerror = () => setListening(false);
    rec.onend = () => setListening(false);
    recRef.current = rec;
    rec.start();
    setListening(true);
  }, [listening]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit(input);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 animate-fade-in" />
        <Dialog.Content
          className="fixed top-0 right-0 bottom-0 z-50 w-full max-w-lg bg-[#0B1220] border-l border-white/10 shadow-2xl flex flex-col animate-slide-in-right"
          data-testid="add-via-ai-dialog"
        >
          {/* Header */}
          <div className="flex items-center gap-3 px-6 py-5 border-b border-white/10 bg-[#0B1220]/95 backdrop-blur-sm flex-shrink-0">
            <div className="w-9 h-9 rounded-full bg-teal-500/15 border border-teal-500/25 flex items-center justify-center">
              <Sparkles size={16} className="text-teal-400" />
            </div>
            <Dialog.Title className="flex-1 text-lg font-extrabold text-white tracking-tight">
              Add via AI
            </Dialog.Title>
            <Dialog.Close
              data-testid="add-via-ai-close"
              className="p-2 rounded-xl hover:bg-white/10 text-white/60 hover:text-white transition-colors"
            >
              <X size={20} />
            </Dialog.Close>
          </div>

          {/* Chat area */}
          <div
            ref={chatRef}
            className="flex-1 overflow-y-auto p-5 space-y-3"
          >
            {messages.map((m, i) => (
              <MsgBubble
                key={i}
                msg={m}
                onApprove={approve}
                busy={isTyping || isExecuting}
              />
            ))}

            {/* Typing indicator */}
            {isTyping && (
              <div className="flex items-center gap-2 self-start px-4 py-3 rounded-2xl rounded-bl bg-white/[0.06] border border-teal-500/15 max-w-[88%]">
                <div className="flex gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-teal-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-teal-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-teal-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
                <span className="text-white/40 text-xs font-medium">Understanding…</span>
              </div>
            )}
          </div>

          {/* Composer */}
          <div className="flex items-center gap-2 px-5 py-4 border-t border-white/10 bg-[#0B1220]/95 flex-shrink-0">
            <input
              ref={inputRef}
              data-testid="add-via-ai-input"
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={sessionId ? 'Type your answer…' : 'What would you like to add?'}
              disabled={isTyping || isExecuting}
              className="flex-1 h-11 px-4 rounded-full bg-white/[0.06] border border-white/10 text-white text-sm font-medium placeholder:text-white/30 focus:outline-none focus:border-teal-500/40 disabled:opacity-50 transition-colors"
              autoComplete="off"
            />
            {SpeechRec && (
              <button
                data-testid="add-via-ai-mic"
                onClick={startVoice}
                disabled={isTyping || isExecuting || listening}
                className={`w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
                  listening
                    ? 'bg-red-500/20 border border-red-500/30'
                    : 'bg-teal-500/15 border border-teal-500/25 hover:bg-teal-500/25'
                } disabled:opacity-40`}
              >
                <Mic size={16} className={listening ? 'text-red-400' : 'text-teal-400'} />
              </button>
            )}
            <button
              data-testid="add-via-ai-send"
              onClick={() => submit(input)}
              disabled={isTyping || isExecuting || !input.trim()}
              className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 bg-gradient-to-br from-cyan-400 to-teal-500 text-white disabled:opacity-40 hover:brightness-110 transition-all"
            >
              <Send size={16} />
            </button>
          </div>

          {/* Bloom overlay */}
          {bloomVisible && (
            <div
              className="absolute inset-0 flex items-center justify-center z-[60] pointer-events-none ai-bloom-overlay"
              data-testid="add-via-ai-bloom"
            >
              <div className="ai-bloom-glow" />
              <div className="ai-bloom-circle">
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none" className="ai-bloom-check">
                  <path
                    d="M12 24L21 33L36 15"
                    stroke="white"
                    strokeWidth="4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="ai-bloom-checkpath"
                  />
                </svg>
              </div>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/* ── Message Bubble ── */
function MsgBubble({ msg, onApprove, busy }) {
  if (msg.role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] px-4 py-3 rounded-2xl rounded-br bg-teal-600 text-white text-sm font-semibold leading-relaxed">
          {msg.text}
        </div>
      </div>
    );
  }

  const isError = msg.kind === 'error';
  const isReady = msg.kind === 'ready';

  return (
    <div className="flex justify-start">
      <div className={`max-w-[88%] px-4 py-3 rounded-2xl rounded-bl border ${
        isError
          ? 'bg-red-500/[0.08] border-red-500/20'
          : 'bg-white/[0.06] border-teal-500/15'
      }`}>
        <p className={`text-sm font-medium leading-relaxed ${isError ? 'text-red-300' : 'text-white/80'}`}>
          {msg.text}
        </p>

        {isReady && msg.preview && (
          <PreviewCard
            preview={msg.preview}
            onApprove={onApprove}
            busy={busy}
          />
        )}
      </div>
    </div>
  );
}

/* ── Preview Card ── */
function PreviewCard({ preview, onApprove, busy }) {
  const { formatMoney: fmtINR } = useTenantFormat();
  return (
    <div
      className="mt-3 p-4 rounded-2xl bg-teal-500/[0.08] border border-teal-500/25"
      data-testid="add-via-ai-preview"
    >
      {/* Line items */}
      {preview.line_items?.length > 0 && (
        <div className="space-y-1.5 mb-3">
          {preview.line_items.map((li, i) => (
            <div key={i} className="flex justify-between items-center text-xs">
              <span className="text-white/70 font-medium">
                {li.quantity}× {li.product_name}
                {li.unit_price != null ? ` @ ${fmtINR(li.unit_price)}` : ''}
              </span>
              {li.total != null && (
                <span className="text-white font-bold tabular-nums">{fmtINR(li.total)}</span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Generic fields (stock adjustments, etc.) */}
      {!preview.line_items && preview.product_name && (
        <div className="mb-3 text-xs text-white/70 font-medium space-y-0.5">
          {preview.product_name && <p>· {preview.product_name}</p>}
          {preview.quantity != null && <p>· Qty: {preview.quantity}</p>}
          {preview.delta != null && <p>· Delta: {preview.delta > 0 ? '+' : ''}{preview.delta}</p>}
          {preview.reason && <p>· {preview.reason}</p>}
        </div>
      )}

      {/* Vendor */}
      {preview.vendor_name && (
        <p className="text-xs text-white/50 mb-3">Vendor: {preview.vendor_name}</p>
      )}

      {/* Total */}
      {preview.total_rupees != null && (
        <p className="text-white font-extrabold text-base tabular-nums mb-3 border-t border-teal-500/15 pt-2">
          Total · {fmtINR(preview.total_rupees)}
        </p>
      )}

      {/* Customer / payment */}
      {preview.customer_name && (
        <p className="text-xs text-white/50 mb-3">Customer: {preview.customer_name}</p>
      )}

      {/* Actions */}
      <div className="flex gap-2">
        <button
          data-testid="add-via-ai-approve"
          onClick={onApprove}
          disabled={busy}
          className="flex-1 h-11 rounded-xl bg-emerald-500 text-white font-extrabold text-sm hover:bg-emerald-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {busy ? (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              <Check size={16} />
              Approve & Save
            </>
          )}
        </button>
        <button
          data-testid="add-via-ai-cancel"
          onClick={() => {/* Cancel is no-op in chat — user can type more */}}
          disabled={busy}
          className="px-4 h-11 rounded-xl bg-white/5 border border-white/10 text-white/60 font-bold text-sm hover:bg-white/10 transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
